import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import "./LandingNav.css";

const MOBILE_QUERY = "(max-width: 767px)";

function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);
    setMatches(mediaQuery.matches);
    mediaQuery.addEventListener?.("change", handleChange);
    return () => mediaQuery.removeEventListener?.("change", handleChange);
  }, [query]);

  return matches;
}

export default function LandingNav({ primaryItems, ctaItem }) {
  const location = useLocation();
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const [menuOpen, setMenuOpen] = useState(false);
  const shellRef = useRef(null);
  const menuButtonRef = useRef(null);

  const closeMenu = useCallback((restoreFocus = false) => {
    setMenuOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => menuButtonRef.current?.focus());
    }
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeMenu(true);
    };
    const handlePointerDown = (event) => {
      if (!shellRef.current?.contains(event.target)) closeMenu(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [closeMenu, menuOpen]);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  const renderPrimaryLink = (item, mobile = false) => (
    <Link
      className={mobile ? "landing-mobile-link" : "landing-nav-link"}
      key={item.label}
      to={item.to}
      aria-current={location.pathname === item.to ? "page" : undefined}
      onClick={() => mobile && closeMenu(false)}
    >
      {item.label}
    </Link>
  );

  const handleCtaClick = (mobile = false) => {
    if (mobile) closeMenu(true);

    if (location.pathname === "/" && ctaItem.to.includes("#")) {
      const targetId = ctaItem.to.split("#")[1];
      window.requestAnimationFrame(() => {
        document.getElementById(targetId)?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches
            ? "auto"
            : "smooth",
          block: "start",
        });
      });
    }
  };

  if (isMobile) {
    return (
      <>
        <Link
          className="landing-mobile-brand-link"
          to="/"
          aria-label="The World of the Pehuans home"
          onClick={() => closeMenu(false)}
        >
          <span className="landing-mobile-brand-surface">
            <img
              className="landing-mobile-brand-logo"
              src="/media/images/enter_logo.avif"
              alt=""
              width="140"
              height="44"
              decoding="async"
            />
          </span>
        </Link>

        <header className="landing-nav-shell is-mobile" ref={shellRef}>
          <button
            ref={menuButtonRef}
            type="button"
            className="landing-menu-trigger"
            aria-expanded={menuOpen}
            aria-controls="oathbound-mobile-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
            <span>{menuOpen ? "Close" : "Menu"}</span>
          </button>

          {menuOpen && (
            <nav
              id="oathbound-mobile-menu"
              className="landing-mobile-menu"
              aria-label="Oathbound navigation"
            >
              {primaryItems.map((item) => renderPrimaryLink(item, true))}
              <Link
                className="landing-mobile-link is-cta"
                to={ctaItem.to}
                onClick={() => handleCtaClick(true)}
              >
                {ctaItem.label}
              </Link>
            </nav>
          )}
        </header>
      </>
    );
  }

  return (
    <header className="landing-nav-shell">
      <nav className="landing-desktop-nav" aria-label="Oathbound navigation">
        <div className="landing-brand">
          <img
            className="landing-brand-logo"
            src="/media/images/enter_logo.avif"
            alt="The World of the Pehuans"
            width="140"
            height="44"
            decoding="async"
          />
        </div>
        <div className="landing-primary-links">
          {primaryItems.map((item) => renderPrimaryLink(item))}
        </div>
        <Link
          className="landing-buy-link"
          to={ctaItem.to}
          onClick={() => handleCtaClick(false)}
        >
          {ctaItem.label}
        </Link>
      </nav>
    </header>
  );
}
